import { ComponentType, ReactNode } from 'react';

import {
  AudioLinesIcon,
  CirclePlayIcon,
  FileTextIcon,
  GalleryHorizontalIcon,
  ImageIcon,
  LinkIcon,
  ListChecksIcon,
  MonitorSmartphoneIcon,
  NotepadTextIcon,
  PanelTopIcon,
  SquareArrowOutUpRightIcon,
  TypeIcon,
  WorkflowIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';
import { ResponseMessage } from '@/shared/types/tryIt';

type Props = {
  icon: ComponentType<{ size?: number; className?: string }>;
  label: string;
  onClick?: () => void;
  href?: string;
};

const BASE = 'flex w-fit items-center gap-1 text-xs leading-4 font-medium text-muted-foreground';
const HOVER = '-m-1 rounded-md p-1 transition hover:bg-selected-soft';

/** Names the node a response came from, and links to it when the node is reachable. */
export const ResponseNodeLabel = ({ icon: Icon, label, onClick, href }: Props) => {
  const { t } = useTranslation();

  const content = (
    <>
      <Icon size={12} className="shrink-0" />
      <span>{label}</span>
      {(href || onClick) && <SquareArrowOutUpRightIcon size={12} className="shrink-0" />}
    </>
  );

  if (!href && !onClick) {
    return <div className={BASE}>{content}</div>;
  }

  return (
    <InfoTooltip content={t('try_it.go_to_dialog_node')} side="left">
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={cn(BASE, HOVER)}>
          {content}
        </a>
      ) : (
        <button type="button" onClick={onClick} className={cn(BASE, HOVER)}>
          {content}
        </button>
      )}
    </InfoTooltip>
  );
};

// Icons match the dialog editor's own action icons (`iconByType`).
export const NODE_LABELS: Partial<
  Record<ResponseMessage['type'], { icon: ComponentType<{ size?: number }>; labelKey: string }>
> = {
  text: { icon: TypeIcon, labelKey: 'try_it.node_label.text' },
  carousel: { icon: GalleryHorizontalIcon, labelKey: 'try_it.node_label.carousel' },
  image: { icon: ImageIcon, labelKey: 'try_it.node_label.image' },
  video: { icon: CirclePlayIcon, labelKey: 'try_it.node_label.video' },
  file: { icon: FileTextIcon, labelKey: 'try_it.node_label.file' },
  url: { icon: LinkIcon, labelKey: 'try_it.node_label.url' },
  whatsapp_flow: { icon: WorkflowIcon, labelKey: 'try_it.node_label.whatsapp_flow' },
  question_set: { icon: ListChecksIcon, labelKey: 'try_it.node_label.question_set' },
  survey: { icon: NotepadTextIcon, labelKey: 'try_it.node_label.survey' },
  webview: { icon: PanelTopIcon, labelKey: 'try_it.node_label.webview' },
  switch_channel: { icon: MonitorSmartphoneIcon, labelKey: 'try_it.node_label.switch_channel' },
  audio: { icon: AudioLinesIcon, labelKey: 'try_it.node_label.audio' },
};

type CardProps = {
  type: string;
  onGoToNode?: () => void;
  nodeHref?: string;
  /** Controls shown on the right of the label row, e.g. the carousel arrows. */
  actions?: ReactNode;
  children: ReactNode;
};

/** One response as its own card in the redesigned turn row, headed by its node. */
export const ResponseCard = ({ type, onGoToNode, nodeHref, actions, children }: CardProps) => {
  const { t } = useTranslation();
  const node = NODE_LABELS[type];

  return (
    <div className="turn-card flex flex-col items-start gap-2 self-stretch">
      {(node || actions) && (
        <div className="flex h-6 shrink-0 items-start gap-2 self-stretch">
          {node && (
            <ResponseNodeLabel
              icon={node.icon}
              label={t(node.labelKey)}
              onClick={onGoToNode}
              href={nodeHref}
            />
          )}
          {actions && <div className="ml-auto flex items-center gap-4">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
};

/** Types that render a card. A pause, tag or webhook arrives but shows nothing. */
export const RENDERED_RESPONSE_TYPES = new Set<ResponseMessage['type']>(
  Object.keys(NODE_LABELS) as ResponseMessage['type'][]
);
